import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, Link, useNavigate, Navigate } from "react-router-dom";
import {
  BarChart3, CreditCard, GraduationCap, LayoutDashboard, LogOut, Package,
  ReceiptText, Settings, ShoppingBag, Store, Users, LifeBuoy, Wallet, Menu, X, Home, TrendingUp, Clock, Lock, Sparkles,
  KeyRound, PackageCheck, UserCheck, Percent, Star, Gift, Megaphone, MessageCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { applyMenuLayout } from "@/lib/menuLayout";
import { useVendorMe, useVendorBadges } from "@/hooks/useVendor";
import NotificationBell from "@/components/ai/NotificationBell";
import { OrderAlarmProvider, OrderAlarmSettings } from "@/context/OrderAlarmContext";

export function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    if (!targetIso) return null;
    const diff = new Date(targetIso).getTime() - now;
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds, done: false };
  }, [targetIso, now]);
}

export function TrialCountdown({ targetIso, compact = false }) {
  const c = useCountdown(targetIso);
  if (!c) return null;
  const box = (v, l) => (
    <div className="flex flex-col items-center rounded-lg bg-white/15 px-2.5 py-1.5 min-w-[52px]">
      <span className="font-display text-lg font-black leading-none tabular-nums">{String(v).padStart(2, "0")}</span>
      <span className="mt-0.5 text-[9px] font-bold uppercase tracking-wide opacity-80">{l}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-1.5" data-testid="trial-countdown">
      {box(c.days, "jours")}{box(c.hours, "h")}{box(c.minutes, "min")}{!compact && box(c.seconds, "s")}
    </div>
  );
}

const MENU = [
  { to: "/vendeur/dashboard", label: "Tableau de bord", icon: LayoutDashboard, end: true, testId: "vnav-dashboard" },
  { to: "/vendeur/produits", label: "Mes produits", icon: Package, testId: "vnav-products" },
  { to: "/vendeur/licences", label: "Licences & clés", icon: KeyRound, testId: "vnav-licenses" },
  { to: "/vendeur/formations", label: "Mes formations", icon: GraduationCap, testId: "vnav-formations" },
  { to: "/vendeur/offres", label: "Offres groupées", icon: Gift, testId: "vnav-bundles" },
  { to: "/vendeur/etudiants", label: "Étudiants & accès", icon: UserCheck, testId: "vnav-students", badge: "students_pending" },
  { to: "/vendeur/commandes", label: "Mes commandes", icon: ShoppingBag, testId: "vnav-orders", badge: "orders_todo" },
  { to: "/vendeur/livraisons", label: "Livraisons numériques", icon: PackageCheck, testId: "vnav-deliveries", badge: "deliveries_todo" },
  { to: "/vendeur/paiements-verifier", label: "Paiements à vérifier", icon: ReceiptText, testId: "vnav-payments-verify", badge: "payments_review" },
  { to: "/vendeur/paiements", label: "Moyens de paiement", icon: CreditCard, testId: "vnav-payments" },
  { to: "/vendeur/clients", label: "Mes clients", icon: Users, testId: "vnav-clients" },
  { to: "/vendeur/ventes", label: "Mes ventes", icon: TrendingUp, testId: "vnav-sales" },
  { to: "/vendeur/promos", label: "Codes promo", icon: Percent, testId: "vnav-promos" },
  { to: "/vendeur/publications", label: "Publications & Promotions", icon: Megaphone, testId: "vnav-publications" },
  { to: "/vendeur/avis", label: "Avis clients", icon: Star, testId: "vnav-reviews" },
  { to: "/vendeur/statistiques", label: "Statistiques", icon: BarChart3, testId: "vnav-stats" },
  { to: "/vendeur/ia", label: "IA & Analytics", icon: Sparkles, testId: "vnav-ai" },
  { to: "/vendeur/abonnement", label: "Mon abonnement", icon: Wallet, testId: "vnav-subscription" },
  { to: "/vendeur/notes", label: "Mes notes privées", icon: Lock, testId: "vnav-notes" },
  { to: "/vendeur/boutique", label: "Ma boutique", icon: Store, testId: "vnav-shop" },
  { to: "/vendeur/profil", label: "Profil", icon: Settings, testId: "vnav-profile" },
  { to: "/vendeur/support", label: "Support", icon: LifeBuoy, testId: "vnav-support" },
];

const BOTTOM = [
  { to: "/vendeur/dashboard", label: "Accueil", icon: Home, end: true },
  { to: "/vendeur/produits", label: "Produits", icon: Package },
  { to: "/vendeur/commandes", label: "Commandes", icon: ShoppingBag },
  { to: "/vendeur/ventes", label: "Ventes", icon: TrendingUp },
  { to: "/vendeur/profil", label: "Compte", icon: Settings },
];

function StatusBanner({ status }) {
  if (!status) return null;
  if (status.is_blocked) {
    return (
      <div className="mb-5 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="vendor-blocked-banner">
        <Lock className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
        <div>
          <p className="font-display text-sm font-bold text-rose-800">Votre compte revendeur a été temporairement suspendu.</p>
          <p className="mt-1 text-xs text-rose-700">Contactez l'administrateur pour plus d'informations.</p>
        </div>
      </div>
    );
  }
  if (status.status === "expired") {
    return (
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="vendor-expired-banner">
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="font-display text-sm font-bold text-amber-900">Votre période d'essai est terminée.</p>
            <p className="mt-1 text-xs text-amber-800">Choisissez un abonnement pour continuer à vendre sur ISMO-TECH.</p>
          </div>
        </div>
        <Link to="/vendeur/abonnement" data-testid="vendor-expired-cta" className="rounded-full bg-amber-600 px-4 py-2 text-xs font-bold text-white hover:bg-amber-700">
          Choisir un abonnement
        </Link>
      </div>
    );
  }
  if (status.status === "trial") {
    return (
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-brand to-[#0036B4] p-4 text-white" data-testid="vendor-trial-banner">
        <div>
          <p className="font-display text-sm font-bold">🎁 Votre période d'essai gratuit est active.</p>
          <p className="mt-0.5 text-xs text-white/80">Profitez de toutes les fonctionnalités de vente sans engagement.</p>
        </div>
        <TrialCountdown targetIso={status.trial_ends_at} />
      </div>
    );
  }
  return null;
}

function WhatsAppVerifyBanner({ vendor }) {
  if (!vendor || vendor.phone_verified) return null;
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="vendor-whatsapp-verify-banner">
      <div className="flex items-start gap-3">
        <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <p className="font-display text-sm font-bold text-amber-900">Confirmez votre numéro WhatsApp</p>
          <p className="mt-0.5 text-xs text-amber-800">Vérifiez votre compte WhatsApp pour recevoir sans faute les alertes de commande de votre boutique.</p>
        </div>
      </div>
      <Link to="/vendeur/profil" data-testid="vendor-whatsapp-verify-cta" className="rounded-full bg-amber-600 px-4 py-2 text-xs font-bold text-white hover:bg-amber-700">
        Vérifier maintenant
      </Link>
    </div>
  );
}

export default function VendorLayout() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: vendor } = useVendorMe(!!user && user.role === "vendor");
  const { data: badges } = useVendorBadges();
  const [menuLayout, setMenuLayout] = useState(null);

  useEffect(() => {
    if (user && user.role === "vendor") {
      api.get("/vendor/menu-layout").then(({ data }) => setMenuLayout(data)).catch(() => {});
    }
  }, [user]);

  const displayMenu = applyMenuLayout(MENU, menuLayout);

  if (loading) return <div className="flex min-h-screen items-center justify-center text-slate-400">Chargement…</div>;
  if (!user) return <Navigate to="/connexion" replace state={{ from: "/vendeur/dashboard" }} />;
  if (user.role !== "vendor") return <Navigate to="/devenir-revendeur" replace />;

  const status = vendor?.status ? vendor : null;

  return (
    <OrderAlarmProvider role="vendor" ordersPath="/vendeur/commandes">
    <div className="min-h-screen bg-surface">
      <header className="sticky top-0 z-40 border-b border-line bg-white">
        <div className="mx-auto flex h-16 max-w-[1500px] items-center gap-3 px-4 sm:px-6">
          <button onClick={() => setOpen(true)} data-testid="vendor-mobile-menu" className="lg:hidden -ml-1 flex h-9 w-9 items-center justify-center rounded-lg text-ink active:bg-slate-100">
            <Menu className="h-6 w-6" />
          </button>
          <Link to="/vendeur/dashboard" className="flex items-center gap-2.5" data-testid="vendor-logo">
            <img src="/logo-mark.png" alt="ISMO-TECH" className="h-9 w-auto shrink-0 object-contain" />
            <span className="font-display text-lg font-extrabold text-ink">
              ISMO<span className="text-brand">-TECH</span>
              <span className="ml-2 rounded bg-brand-light px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand">Vendeur</span>
            </span>
          </Link>
          <div className="ml-auto flex items-center gap-3">
            <OrderAlarmSettings />
            <NotificationBell />
            <div className="hidden text-right sm:block">
              <p className="text-xs font-bold text-ink">{vendor?.shop_name || "Ma boutique"}</p>
              <p className="text-[11px] text-slate-500">{user.email}</p>
            </div>
            <Link to="/" className="hidden rounded-full border border-line px-3 py-1.5 text-xs font-bold text-ink hover:border-brand hover:text-brand sm:block" data-testid="vendor-view-site">Voir le site</Link>
            <button data-testid="vendor-logout" onClick={async () => { await logout(); navigate("/"); }} className="flex h-9 w-9 items-center justify-center rounded-lg text-rose-600 hover:bg-rose-50">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1500px] gap-6 px-4 py-6 sm:px-6">
        <aside className="hidden w-60 shrink-0 lg:block">
          <nav className="sticky top-[88px] space-y-1">
            {displayMenu.map(({ to, label, icon: Icon, end, testId, badge }) => (
              <NavLink key={to} to={to} end={end} data-testid={testId}
                className={({ isActive }) => `flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-[13.5px] font-semibold transition-colors duration-200 ${isActive ? "bg-brand text-white" : "text-slate-600 hover:bg-white hover:text-ink"}`}>
                <Icon className="h-4 w-4" /> <span className="flex-1">{label}</span>
                {badge && badges?.[badge] > 0 && (
                  <span data-testid={`badge-${badge}`} className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-black text-white">
                    {badges[badge]}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>

        <div className="min-w-0 flex-1 pb-24 lg:pb-0">
          <WhatsAppVerifyBanner vendor={vendor} />
          <StatusBanner status={status} />
          <Outlet context={{ vendor }} />
        </div>
      </div>

      {/* mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[80] lg:hidden" data-testid="vendor-drawer">
          <div className="absolute inset-0 bg-ink/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[82%] max-w-[320px] overflow-y-auto bg-white p-4 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-display font-extrabold text-ink">Menu vendeur</span>
              <button onClick={() => setOpen(false)} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500"><X className="h-5 w-5" /></button>
            </div>
            {displayMenu.map(({ to, label, icon: Icon, end, badge }) => (
              <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
                className={({ isActive }) => `flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-sm font-semibold ${isActive ? "bg-brand text-white" : "text-slate-600"}`}>
                <Icon className="h-4 w-4" /> <span className="flex-1">{label}</span>
                {badge && badges?.[badge] > 0 && (
                  <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-black text-white">
                    {badges[badge]}
                  </span>
                )}
              </NavLink>
            ))}
          </aside>
        </div>
      )}

      {/* mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex border-t border-line bg-white lg:hidden" data-testid="vendor-bottom-nav">
        {BOTTOM.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end}
            className={({ isActive }) => `flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-bold ${isActive ? "text-brand" : "text-slate-500"}`}>
            <Icon className="h-5 w-5" /> {label}
          </NavLink>
        ))}
      </nav>
    </div>
    </OrderAlarmProvider>
  );
}

export { MENU as VENDOR_MENU };
